/**
 * Gaming stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const gamingStickersPack: ToilePack = {
    slug: 'gaming-stickers',
    category: 'sticker',
    name: 'Gaming stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'controller', label: 'Controller', subject: 'a game controller with two sticks and four buttons'},
        {key: 'heart', label: 'Pixel heart', subject: 'a pixel heart in crisp square pixels'},
        {key: 'gameover', label: 'Game over', subject: 'the words "GAME OVER" in blocky arcade lettering'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'vinyl', label: 'Vinyl', style: 'glossy vinyl sticker, clean vector shapes, bold flat colour, a small highlight', palette: 'Bright flat colour with a white highlight', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
    ],
    directions: ['a joystick instead', 'a whole sticker sheet', 'as glow-in-the-dark stickers'],
    directionHint: 'try: a joystick instead',
    seo: {
        title: 'Gaming Stickers: Controller, Pixel Heart, Game Over',
        metaDescription: 'Nine gaming stickers — a controller, a pixel heart, GAME OVER lettering — in holographic, kawaii, and vinyl styles.',
        h1: 'Gaming stickers: controller, pixel heart, game over',
        tagline: 'Nine stickers, three gamer icons, three styles each.',
        intro: "A game controller with two sticks and four buttons, a pixel heart in crisp squares, and the words 'GAME OVER' in blocky arcade lettering — three gamer stickers, die-cut and ready to print. Each rendered holographic, kawaii, and glossy vinyl. Ask for a joystick instead, a retro-arcade scene, or a level-up composition, and the same three styles carry it.",
        useCases: [
            'a laptop or console lid',
            'a phone case sticker sheet',
            'a water bottle or notebook',
            'a gift for a gamer',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine gaming stickers — a controller, a pixel heart, and GAME OVER lettering — in holographic, kawaii, and glossy vinyl finishes.'},
            {q: 'Can I get a joystick or a different scene?', a: 'Yes — the direction field takes any variation, including a joystick instead, a retro-arcade scene, or a level-up composition.'},
            {q: 'What does the holographic finish print as?', a: 'The holographic design is an illustrated iridescent sheen — print it on holographic vinyl stock for the real shifting effect, or on plain matte stock for a flat colourful version.'},
            {q: 'Does the controller show a real console brand?', a: "No — it's a generic controller silhouette, not a licensed console or brand."},
        ],
    },
}
